import { authService } from "@/services/auth/authService";
import { User } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { NavLink, useNavigate, useRevalidator } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

const navItems = [
  {
    path: "/settings",
    textKey: "nav.settings",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        className="size-7"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
        />
      </svg>
    ),
  },
];

type navbarProfileSectionProps = {
  userName: string;
};

interface NavItemProps {
  path: string;
  textKey: string;
  icon: React.ReactElement;
}

const NavItem: React.FC<NavItemProps> = ({ path, textKey, icon }) => {
  const { t } = useTranslation();
  return (
    <NavLink
      to={path}
      className={({ isActive }) =>
        `flex gap-2 items-center p-3 transition-colors duration-200 ${
          isActive
            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400"
            : "hover:bg-gray-100 dark:hover:bg-gray-700"
        }`
      }
    >
      {icon}
      <p className="text-base font-semibold">{t(textKey)}</p>
    </NavLink>
  );
};

const NavbarProfileSection = ({ userName }: navbarProfileSectionProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const logoutHandler = async () => {
    setIsMenuOpen(false);
    await authService.logout();
    navigate("/homepage", { replace: true });
    revalidator.revalidate();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="flex items-center relative" ref={menuRef}>
      <NavLink
        to="/settings"
        onClick={(e) => {
          e.preventDefault();
          toggleMenu();
        }}
        aria-label="Toggle menu"
        className={({ isActive }) =>
          `flex items-center justify-center rounded-md transition-colors duration-200 cursor-pointer ${
            isActive
              ? "text-primary-600 dark:text-primary-400"
              : "hover:bg-accent hover:text-accent-foreground"
          }`
        }
      >
        <User />
        <div className="flex-col p-1">
          <p className="text-sm">{userName}</p>
        </div>
      </NavLink>

      {/* Dropdown Menu */}
      <div
        className={`
          bg-white absolute top-full border-amber-300 right-0 mt-2 w-48 rounded-md shadow-lg z-50
          ${isMenuOpen ? "block" : "hidden"}
        `}
      >
        {navItems.map((item) => (
          <NavItem key={item.path} {...item} />
        ))}
        <button
          className="flex w-full gap-2 items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
          onClick={logoutHandler}
        >
          <LogOut className="w-6 h-6" />
          <p className="text-base font-semibold">{t("nav.logout")}</p>
        </button>
      </div>
    </div>
  );
};
export default NavbarProfileSection;
